import { useEffect, useState } from "react";
import { Search, RotateCcw, Trash2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { GlassCard, PageHeader, EmptyState } from "../components/shared";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
        const date = new Date(dateStr);
        const day = date.getDate();
        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const month = months[date.getMonth()];
        const year = date.getFullYear();
        return `${day} ${month} ${year}`;
    } catch (e) {
        return dateStr;
    }
};

const getInitials = (name) => {
    if (!name) return "EE";
    const parts = String(name).trim().split(/\s+/);
    if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return String(name).substring(0, 2).toUpperCase();
};

export default function Alumni() {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);
    const canManage = ["super_admin", "hr", "founder"].includes(user?.role);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/employees?status=archived&q=${encodeURIComponent(q)}`);
            setItems(data.items || []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onSearch = (e) => {
        e.preventDefault();
        load();
    };

    const restoreEmployee = async (emp) => {
        if (!window.confirm(`Are you sure you want to restore ${emp.name} to active status?`)) return;
        try {
            await api.patch(`/employees/${emp.id}`, { status: "active" });
            toast.success("Employee restored successfully");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const removeEmployee = async (id) => {
        if (!window.confirm("Are you sure you want to delete this employee? This will permanently delete their account and attendance data.")) return;
        try {
            await api.delete(`/employees/${id}`);
            toast.success("Employee permanently deleted");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <div>
            <PageHeader 
                title="Alumni & Past Employees" 
                subtitle="View records of completed internships and past team members." 
            />

            <GlassCard className="p-6 mb-6">
                <form onSubmit={onSearch} className="flex gap-3">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input 
                            placeholder="Search alumni by name, email or employee ID..." 
                            className="pl-9" 
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                        />
                    </div>
                    <Button type="submit" className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl px-6 font-semibold">
                        Search
                    </Button>
                </form>
            </GlassCard>

            {loading ? (
                <div className="text-center py-12 text-slate-400">Loading records...</div>
            ) : items.length === 0 ? (
                <EmptyState 
                    title="No alumni records found" 
                    subtitle="Past or archived employees will appear here." 
                />
            ) : (
                <GlassCard className="overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Employee</TableHead>
                                <TableHead>ID</TableHead>
                                <TableHead>Role</TableHead>
                                <TableHead>Department</TableHead>
                                <TableHead>Position</TableHead>
                                <TableHead>Joined</TableHead>
                                {canManage && <TableHead className="text-right">Actions</TableHead>}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {items.map((e) => {
                                const initials = getInitials(e.name);
                                return (
                                    <TableRow key={e.id}>
                                        <TableCell>
                                            <div className="flex items-center gap-3">
                                                {e.avatar ? (
                                                    <img src={e.avatar} alt={e.name} className="w-10 h-10 rounded-full object-cover border border-black/5" />
                                                ) : (
                                                    <div className="w-10 h-10 rounded-full bg-slate-900 text-white grid place-items-center text-xs font-semibold uppercase shadow-sm">
                                                        {initials}
                                                    </div>
                                                )}
                                                <div>
                                                    <div className="font-semibold text-gray-900 dark:text-gray-100">{e.name}</div>
                                                    <div className="text-xs text-muted-foreground">{e.email}</div>
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell className="font-mono text-xs font-medium">{e.employee_id}</TableCell>
                                        <TableCell>
                                            <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                                                {e.role}
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-xs font-medium text-slate-600 dark:text-slate-300">{e.department || "—"}</TableCell>
                                        <TableCell className="text-xs font-medium text-slate-600 dark:text-slate-300">{e.designation || "—"}</TableCell>
                                        <TableCell className="text-xs font-medium text-slate-500">{formatDate(e.joining_date)}</TableCell>
                                        {canManage && (
                                            <TableCell className="text-right">
                                                <div className="flex justify-end gap-2">
                                                    <Button 
                                                        size="sm" 
                                                        variant="outline" 
                                                        onClick={() => restoreEmployee(e)}
                                                        className="h-8 rounded-lg flex items-center gap-1 text-xs"
                                                    >
                                                        <RotateCcw className="w-3.5 h-3.5" /> Restore
                                                    </Button>
                                                    <Button 
                                                        size="sm" 
                                                        variant="destructive" 
                                                        onClick={() => removeEmployee(e.id)}
                                                        className="h-8 rounded-lg flex items-center gap-1 text-xs"
                                                    >
                                                        <Trash2 className="w-3.5 h-3.5" /> Delete
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </GlassCard>
            )}
        </div>
    );
}
